import { consoleEvents, LoadingStates } from '@pb/utils'
import { comparator, compose, pick, reject, sortWith, uniq } from 'ramda'
import { createReducer } from 'redux-act'

import getFirstMessageFromMessagesArray from 'helpers/getFirstMessageFromMessagesArray'

import { CartState } from '../helpers/types'
import * as actions from './actions'
import initialState from './initialState'

const fetchCartStart = (state): any => ({
  ...state,
  isCartLoading: true,
})

const mapAndSortBrands = (items): any => {
  const hmBrand = 'H&M'

  // item.brand will not have a value in the API response if there are only H&M items in the cart
  const brands = items.map((item) => item.brand).filter((brand) => !!brand)

  // When deleting an item we may get only items with brand 'H&M' left, for which we should return []
  return !brands.every((brand) => brand === hmBrand)
    ? compose(
        // If brand 'H&M' exists, it should be first. Otherwise, alphabetical ordering.
        sortWith([
          (a, b) => (a === hmBrand ? -1 : b === hmBrand ? 1 : 0),
          // @ts-expect-error ts-migrate(2571) FIXME: Object is of type 'unknown'.
          comparator((a, b) => a.toLowerCase() < b.toLowerCase()),
        ]),
        uniq,
      )(brands)
    : []
}

const onCartUpdateSuccess = (state, payload): any => {
  const propertiesToStore = [
    'cartCode',
    'cartOwner',
    'codLimitReached',
    'deliveryAddress',
    'fineprintReturnAndRefundRequired',
    'items',
    'oneClickCheckoutInfo',
    'oosItems',
    'hazmatItems',
    'orderTotals',
    'returnAndRefundRequired',
    'returnDays',
    'taxDisclaimerRequired',
    'vatMessageRequired',
    'oneClickCheckoutInfo',
    'vouchersApplied',
    'giftCardsApplied',
    'paymentModes',
    'supportedCreditCards',
  ]

  const newValues = compose(
    pick(propertiesToStore),
    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    reject((value) => typeof value === 'undefined'),
    // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
  )(payload)

  const message = getFirstMessageFromMessagesArray(payload.messagesMap)
  const messageCodeAvailabilityChanged = '1010'

  // @ts-expect-error ts-migrate(6133) FIXME: 'brands' is declared but its value is never read.
  const brands = mapAndSortBrands(newValues.items)

  return {
    ...state,
    // @ts-expect-error ts-migrate(2698) FIXME: Spread types may only be created from object types... Remove this comment to see the full error message
    ...newValues,
    brands,
    isCartLoading: payload.availabilityCheck ? false : state.isCartLoading,
    isCartLoadingFailed: payload.availabilityCheck ? false : state.isCartLoadingFailed,
    isCartPopulated: payload.availabilityCheck ? true : state.isCartPopulated,
    infoMessage: message || state.infoMessage,
    ...(message &&
      message.code === messageCodeAvailabilityChanged && {
        modalError: {
          ...message,
          showError: true,
        },
      }),
  }
}

const onPartialCartUpdateSuccess = (state, payload): any => {
  const propertiesToStore = ['orderTotals', 'items']

  const newValues = propertiesToStore.reduce(
    (prev, property) => ({
      ...prev,
      [property]: payload[property],
    }),
    {},
  )

  return {
    ...state,
    ...newValues,
    isCartLoading: false,
    isCartLoadingFailed: false,
    isCartPopulated: true,
  }
}

const fetchCartFail = (state, error): any => {
  consoleEvents.error('Failed to fetch cart', error)
  return {
    ...state,
    isCartLoading: false,
    isCartLoadingFailed: true,
  }
}

const setPickUpInStoreData = (state, pickUpInStoreData): CartState => {
  return {
    ...state,
    pickUpInStoreData,
  }
}

const updateCartItemQuantityStart = (state, { variantCode, quantity }): any => {
  const itemIndex = state.items.findIndex((item) => item.variantCode === variantCode)

  return {
    ...state,
    items: [
      ...state.items.slice(0, itemIndex),
      { ...state.items[itemIndex], quantity },
      ...state.items.slice(itemIndex + 1),
    ],
    isCartLoading: true,
  }
}

const updateCartItemQuantityFail = (state, error): any => {
  consoleEvents.error('Failed to update cart item', error)
  return {
    ...state,
    isCartLoading: false,
    isCartLoadingFailed: true,
  }
}

const deleteCartItemStart = (state, variantCode): any => {
  const items = state.items.filter((i) => i.variantCode !== variantCode)
  // Update brands when item is deleted.
  const brands = mapAndSortBrands(items)
  return {
    ...state,
    brands,
    items,
    oosItems: state.oosItems.filter((i) => i.variantCode !== variantCode),
    hazmatItems: state.hazmatItems.filter((i) => i.variantCode !== variantCode),
    isCartLoading: true,
  }
}

const deleteCartItemFail = (state, error): any => {
  consoleEvents.error('Failed to delete cart item', error)
  return {
    ...state,
    isCartLoading: false,
    isCartLoadingFailed: true,
  }
}

const moveToFavouriteStart = (state, variantCode): any => {
  const items = state.items.filter((i) => i.variantCode !== variantCode)
  // Update brands when item is removed from cart
  const brands = mapAndSortBrands(items)
  return {
    ...state,
    brands,
    items,
    oosItems: state.oosItems.filter((i) => i.variantCode !== variantCode),
    hazmatItems: state.hazmatItems.filter((i) => i.variantCode !== variantCode),
    isCartLoading: true,
  }
}

const moveToFavouriteFail = (
  state,
  { previousItemsState, previousOosItemsState, previousHazmatItemsState },
): any => {
  consoleEvents.error('Failed to move cart item to favourites')
  return {
    ...state,
    items: [...previousItemsState],
    oosItems: [...previousOosItemsState],
    hazmatItems: [...previousHazmatItemsState],
    isCartLoading: false,
    isCartLoadingFailed: true,
  }
}

const setCartItemPrices = (state, payload): any => {
  const updatedItems = state.items.map((item) => {
    const newItem = payload.items.find((itm) => itm.variantCode === item.variantCode)
    return {
      ...item,
      productPrice: newItem.productPrice,
      totalPrice: newItem.totalPrice,
      strikedPrice: newItem.strikedPrice,
    }
  })
  const updatedOosItems = state.oosItems.map((item) => {
    const newItem = payload.oosItems.find((itm) => itm.variantCode === item.variantCode)
    return {
      ...item,
      productPrice: newItem.productPrice,
      totalPrice: newItem.totalPrice,
      strikedPrice: newItem.strikedPrice,
    }
  })
  const updatedHazmatItemsItems = state.hazmatItems.map((item) => {
    const newItem = payload.hazmatItems.find((itm) => itm.variantCode === item.variantCode)
    return {
      ...item,
      productPrice: newItem.productPrice,
      totalPrice: newItem.totalPrice,
      strikedPrice: newItem.strikedPrice,
    }
  })

  return {
    ...state,
    items: updatedItems,
    oosItems: updatedOosItems,
    hazmatItems: updatedHazmatItemsItems,
  }
}

const setOneClickCheckoutPaymentData = (state, payload): any => {
  return {
    ...state,
    oneClickCheckoutPaymentData: {
      ...payload,
    },
  }
}

const oneClickCheckoutStart = (state): any => ({
  ...state,
  oneClickCheckoutLoading: true,
})

const oneClickCheckoutSuccess = (state): any => ({
  ...state,
  oneClickCheckoutLoading: false,
  oneClickCheckoutCompleted: true,
})

const oneClickCheckoutFail = (state): any => ({
  ...state,
  oneClickCheckoutLoading: false,
  oneClickCheckoutFailed: true,
})

const fetchHiddenCartItems = (state, hiddenItems): any => ({
  ...state,
  hiddenItems,
})

const deleteHiddenCartItem = (state, productCode): any => {
  const itemIndex = state.hiddenItems.findIndex((item) => item.code === productCode)
  const removed = true

  return {
    ...state,
    hiddenItems: [
      ...state.hiddenItems.slice(0, itemIndex),
      { ...state.hiddenItems[itemIndex], removed },
      ...state.hiddenItems.slice(itemIndex + 1),
    ],
  }
}

const moveHiddenCartItem = (state, productCode): any => {
  const itemIndex = state.hiddenItems.findIndex((item) => item.code === productCode)
  const movedToFavourites = true

  return {
    ...state,
    hiddenItems: [
      ...state.hiddenItems.slice(0, itemIndex),
      { ...state.hiddenItems[itemIndex], movedToFavourites },
      ...state.hiddenItems.slice(itemIndex + 1),
    ],
  }
}
const addGiftCardStart = (state): any => ({
  ...state,
  addGiftCardStatus: LoadingStates.Loading,
})

const addGiftCardSuccess = (state, giftCards): any => ({
  ...state,
  addGiftCardStatus: LoadingStates.LoadedSuccess,
  giftCardsApplied: giftCards,
  giftCardError: {
    message: '',
  },
})

const addGiftCardFail = (state, error): any => ({
  ...state,
  addGiftCardStatus: LoadingStates.LoadedFail,
  giftCardError: {
    isWarningMessage: error.isWarningMessage,
    message: error.message,
  },
})

const setMaxGiftCardError = (state, error): any => ({
  ...state,
  addGiftCardStatus: LoadingStates.LoadedFail,
  giftCardError: {
    isWarningMessage: error.isWarningMessage,
    message: error.message,
    isMaxError: true,
  },
})

const removeGiftCardStart = (state): any => ({
  ...state,
})

const removeGiftCardSuccess = (state, giftCards): any => ({
  ...state,
  giftCardsApplied: giftCards,
  addGiftCardStatus: LoadingStates.Loading,
  giftCardError: {},
})

const removeGiftCardFail = (state): any => ({
  ...state,
})

const showMiddlePage = (state): any => ({
  ...state,
  isMiddlePage: true,
})

const hideMiddlePage = (state): any => ({
  ...state,
  isMiddlePage: false,
})

const reducer = createReducer<any>((on) => {
  on(actions.fetchCartStart, fetchCartStart)
  on(actions.fetchCartSuccess, onCartUpdateSuccess)
  on(actions.fetchCartFail, fetchCartFail)

  on(actions.updateCartItemQuantityStart, updateCartItemQuantityStart)
  on(actions.updateCartItemQuantitySuccess, onCartUpdateSuccess)
  on(actions.updateCartItemQuantityFail, updateCartItemQuantityFail)

  on(actions.deleteCartItemStart, deleteCartItemStart)
  on(actions.deleteCartItemSuccess, onPartialCartUpdateSuccess)
  on(actions.deleteCartItemFail, deleteCartItemFail)

  on(actions.moveToFavouriteStart, moveToFavouriteStart)
  on(actions.moveToFavouriteSuccess, onPartialCartUpdateSuccess)
  on(actions.moveToFavouriteFail, moveToFavouriteFail)

  on(actions.setCartItemPrices, setCartItemPrices)
  on(actions.setPickUpInStoreData, setPickUpInStoreData)

  on(actions.setOneClickCheckoutPaymentData, setOneClickCheckoutPaymentData)
  on(actions.oneClickCheckoutStart, oneClickCheckoutStart)
  on(actions.oneClickCheckoutSuccess, oneClickCheckoutSuccess)
  on(actions.oneClickCheckoutFail, oneClickCheckoutFail)
  on(actions.fetchHiddenCartItems, fetchHiddenCartItems)
  on(actions.deleteHiddenCartItem, deleteHiddenCartItem)
  on(actions.moveHiddenCartItem, moveHiddenCartItem)

  on(actions.addGiftCardStart, addGiftCardStart)
  on(actions.addGiftCardSuccess, addGiftCardSuccess)
  on(actions.addGiftCardFail, addGiftCardFail)
  on(actions.setMaxGiftCardError, setMaxGiftCardError)
  on(actions.removeGiftCardStart, removeGiftCardStart)
  on(actions.removeGiftCardSuccess, removeGiftCardSuccess)
  on(actions.removeGiftCardFail, removeGiftCardFail)
  on(actions.showMiddlePage, showMiddlePage)
  on(actions.hideMiddlePage, hideMiddlePage)
}, initialState)

export default reducer
